import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AssignmentIndOutlined,
  Block,
  ChevronRight,
  DoneAllOutlined,
  LockOpenOutlined,
  NotificationsOutlined,
  RemoveCircleOutline,
  ReplayOutlined,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useAgentNotifications } from "../../Hooks/AgentNotificationsContext";
import {
  AgentNotification,
  apiErrorMessage,
  getNotifications,
  isLinkable,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../utilities/AgentNotificationsApi";

const typeMeta = (type: string): { icon: React.ReactNode; color: string; caption?: string } => {
  switch (type) {
    case "CV_ASSIGNED":
      return { icon: <AssignmentIndOutlined />, color: "#1976d2" };
    case "CV_UNAVAILABLE":
      return { icon: <Block />, color: "#d32f2f", caption: "No longer available" };
    case "CV_RELEASED":
      return { icon: <LockOpenOutlined />, color: "#ed6c02" };
    case "CV_REOPENED":
      return { icon: <ReplayOutlined />, color: "#2e7d32" };
    case "CV_DECLINED":
      return { icon: <RemoveCircleOutline />, color: "#757575", caption: "Removed from your list" };
    default:
      return { icon: <NotificationsOutlined />, color: "#616161" };
  }
};

const AgentNotificationBell = () => {
  const { unreadCount, setUnreadCount, feedOpen, setFeedOpen } = useAgentNotifications();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [tab, setTab] = useState<"all" | "unread">("all");
  const [items, setItems] = useState<AgentNotification[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    async (pageToLoad: number, append: boolean) => {
      setLoading(true);
      try {
        const result = await getNotifications(pageToLoad, tab === "unread");
        setItems((current) => (append ? [...current, ...(result.page?.data || [])] : result.page?.data || []));
        setPage(result.page?.current_page || pageToLoad);
        setLastPage(result.page?.last_page || 1);
        setUnreadCount(result.unreadCount);
      } catch (error: any) {
        enqueueSnackbar(apiErrorMessage(error, "Could not load notifications."), { variant: "error" });
      } finally {
        setLoading(false);
      }
    },
    [tab, enqueueSnackbar, setUnreadCount]
  );

  // Fetch the feed when opened, when the tab changes, or when the polled count changes while open.
  useEffect(() => {
    if (feedOpen) load(1, false);
  }, [feedOpen, tab, unreadCount, load]);

  const handleItemClick = async (notification: AgentNotification) => {
    if (!notification.is_read) {
      try {
        const result = await markNotificationRead(notification.id);
        setItems((current) =>
          current.map((item) => (item.id === notification.id ? { ...item, ...result.notification } : item))
        );
        setUnreadCount(result.unreadCount);
      } catch (error: any) {
        enqueueSnackbar(apiErrorMessage(error, "Could not mark notification as read."), { variant: "error" });
      }
    }
    if (isLinkable(notification)) {
      setFeedOpen(false);
      navigate(`/admin/agent-cv-assignments/${notification.data?.process_agent_assignment_id}`);
    }
  };

  const handleMarkAll = async () => {
    setSaving(true);
    try {
      const remaining = await markAllNotificationsRead();
      setItems((current) => current.map((item) => ({ ...item, is_read: true })));
      setUnreadCount(remaining);
    } catch (error: any) {
      enqueueSnackbar(apiErrorMessage(error, "Could not update notifications."), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        color="inherit"
        aria-label="notifications"
        onClick={() => setFeedOpen(!feedOpen)}
      >
        <Badge badgeContent={unreadCount} max={99} color="error">
          <NotificationsOutlined />
        </Badge>
      </IconButton>
      <Popover
        open={feedOpen}
        anchorEl={anchorRef.current}
        onClose={() => setFeedOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 380, maxWidth: "95vw", maxHeight: 480 } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 1.5 }}>
          <Typography fontWeight={700}>Notifications</Typography>
          <Button
            size="small"
            startIcon={<DoneAllOutlined />}
            onClick={handleMarkAll}
            disabled={unreadCount === 0 || saving}
          >
            Mark all read
          </Button>
        </Box>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ px: 1 }}>
          <Tab label="All" value="all" />
          <Tab label="Unread" value="unread" />
        </Tabs>
        <Divider />
        <List disablePadding>
          {items.map((notification, index) => {
            const meta = typeMeta(notification.type);
            const linkable = isLinkable(notification);
            return (
              <React.Fragment key={notification.id}>
                <ListItemButton
                  onClick={() => handleItemClick(notification)}
                  sx={{
                    alignItems: "flex-start",
                    bgcolor: notification.is_read ? "transparent" : "rgba(25, 118, 210, 0.06)",
                    cursor: linkable || !notification.is_read ? "pointer" : "default",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: meta.color, mt: 0.5 }}>{meta.icon}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={notification.is_read ? 400 : 700}>
                        {notification.message || "Notification"}
                      </Typography>
                    }
                    secondary={
                      <Typography component="span" variant="caption" color="text.secondary">
                        {moment(notification.created_at).fromNow()}
                        {meta.caption ? ` · ${meta.caption}` : ""}
                      </Typography>
                    }
                  />
                  {linkable && <ChevronRight fontSize="small" sx={{ mt: 0.75, color: "text.secondary" }} />}
                </ListItemButton>
                {index < items.length - 1 && <Divider component="li" />}
              </React.Fragment>
            );
          })}
        </List>
        {loading && (
          <Box sx={{ display: "grid", placeItems: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!loading && !items.length && (
          <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
            {tab === "unread" ? "No unread notifications." : "No notifications yet."}
          </Typography>
        )}
        {!loading && page < lastPage && (
          <Box sx={{ textAlign: "center", py: 1 }}>
            <Button size="small" onClick={() => load(page + 1, true)}>
              Load more
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default AgentNotificationBell;
